import React, { useState, useEffect } from "react";
import _ from "lodash";
import axios from "axios";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/styles";
import { Box, Grid } from "@mui/material";
import {
  Button,
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import useNavigateEntity from "hooks/useNavigateEntity";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { Loading } from "features/common";
import { apiUrl } from "features/configure";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import { formatDate, parseCustomDomainUrl } from "features/helpers/utils";
import IntegrationSuccess from "./IntegrationSuccess";
import IntegrationFailModal from "./IntegrationFailModal";
import { StyledWhiteContainer } from "../common.styles";

const IntegrationJira = ({ isSetting = false }) => {
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName, isCustomDomain] = useOutletContext();
  const { entityConfig, fetchEntityConfig } = useFetchEntityConfig();
  const { navigateEntity } = useNavigateEntity();
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [checkStatus, setCheckStatus] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [isInitialConnecting, setIsInitialConnecting] = useState(true);

  const [jiraConfig, setJiraConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initializeIntegration = async () => {
      if (!isSetting) {
        await connectJiraWorkspace();
        setIsInitialConnecting(false);
        setCheckStatus(true);
      } else {
        await getJiraAppConfig();
      }
    };

    initializeIntegration();
  }, []);

  useEffect(() => {
    let id;
    if (!isSetting && checkStatus) {
      id = setInterval(() => {
        fetchEntityConfig({
          entityName,
        });
        setCheckCount((prevCount) => prevCount + 1);
      }, 1000);
    }
    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [checkStatus]);

  useEffect(() => {
    if (!isSetting && !isInitialConnecting) {
      if (_.get(entityConfig, "serviceModule.jira.connected")) {
        setIsSuccess(true);
        setCheckStatus(false);
      } else if (checkCount > 15) {
        setOpen(true);
        setCheckStatus(false);
      }
    }
  }, [entityConfig, checkCount, isInitialConnecting]);

  const fallbackRoute = () => {
    navigate(
      parseCustomDomainUrl(
        isCustomDomain,
        entityName,
        "/settings/general/integration"
      )
    );
  };

  const connectJiraWorkspace = async () => {
    try {
      const tempCode = searchParams.get("code");
      const state = searchParams.get("state");

      if (!tempCode || !state) {
        fallbackRoute();
      }

      await axios.post(apiUrl + `/api/jira/${entityName}/oauth/callback`, {
        code: tempCode,
      });
      dispatchSnackbar({ message: "Integration success!", variant: "success" });
    } catch (err) {
      dispatchSnackbar({ err });
      fallbackRoute();
    }
  };

  const getJiraAppConfig = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(
        apiUrl + `/api/jira/${entityName}/config`
      );

      setJiraConfig(data.jiraConfig);
    } catch (err) {
      dispatchSnackbar({ err });
      fallbackRoute();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      await axios.post(apiUrl + `/api/jira/${entityName}/disconnect`);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      fallbackRoute();
      setIsLoading(false);
    }
  };

  const renderIntegrationScreen = () => {
    if (isSuccess) {
      return (
        <IntegrationSuccess
          img={
            <img
              src={require(`assets/img/jira-integration-success.svg`).default}
              style={{ height: "210px", margin: 50 }}
            />
          }
          description={formatMessage({
            id: "popup.success.description.jiraIssueSettings",
          })}
          navigateSettingBtnText="Ok"
          isNavigateHome={false}
          btnVariant={VARIANT.CONTAINED}
        />
      );
    }
    return (
      <Loading
        open={true}
        text={
          <div style={{ textAlign: "center" }}>
            {formatMessage({ id: "integration.checkingStatus.description" })}
            <div>
              <Button
                color={COLOR.SECONDARY}
                style={{ margin: 0, marginTop: "20px" }}
                onClick={() =>
                  navigate(
                    parseCustomDomainUrl(
                      isCustomDomain,
                      entityName,
                      "/settings/general/integration"
                    )
                  )
                }
              >
                {formatMessage({ id: "global.btn.backToSetting" })}
              </Button>
            </div>
          </div>
        }
      />
    );
  };

  const renderFailedModal = () => {
    return (
      <IntegrationFailModal
        onRetry={(e) => {
          setOpen(false);
          navigate(
            parseCustomDomainUrl(
              isCustomDomain,
              entityName,
              "/settings/general/integration"
            )
          );
        }}
        open={open}
        setOpen={setOpen}
      />
    );
  };

  function renderIntegrationUpdateScreen() {
    return (
      <>
        <Loading open={isLoading} fullScreen={false} />
        <div className="fade-up">
          <Box
            sx={{
              color: "#A5A5A5",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 14,
              marginRight: 4,
              margin: "12px 0",
              marginTop: "-30px",
            }}
            onClick={() => {
              navigateEntity("/settings/general/integration");
            }}
          >
            {formatMessage({ id: "global.btn.back" })}
            <i
              className="meta-crm-icon-ic_redo font-size-14"
              style={{ marginLeft: 4 }}
            />
          </Box>
          <Box mb="20px" display={"flex"} justifyContent={"space-between"}>
            <Box className="title">{"Jira"}</Box>
            <Button onClick={handleDisconnect}>
              {formatMessage({ id: "global.btn.disconnect" })}
            </Button>
          </Box>
        </div>

        <StyledWhiteContainer
          sx={{ marginBottom: "20px" }}
          className="fade-up-more"
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              {formatMessage({ id: "jiraIssueSettings.labelName.workspace" })}
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  backgroundColor: theme.customColors.grey[200],
                  color: theme.customColors.grey[500],
                  padding: "12px",
                  borderRadius: "3px",
                  marginBottom: "4px",
                }}
              >
                {jiraConfig.workspaceName}
              </Box>

              <Box
                display={"flex"}
                sx={{ fontSize: "12px", alignItems: "center" }}
              >
                <Box color={theme.customColors.grey[600]}>
                  {formatMessage({ id: "global.header.createdTime" })} :
                </Box>
                <Box ml={"5px"} color={theme.customColors.purple[500]}>
                  {formatDate(jiraConfig.created, { time: true })}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={2}></Grid>
          </Grid>
        </StyledWhiteContainer>
      </>
    );
  }

  return (
    <>
      {!isSetting ? (
        <>
          {renderFailedModal()}
          {renderIntegrationScreen()}
        </>
      ) : (
        renderIntegrationUpdateScreen()
      )}
    </>
  );
};

export default IntegrationJira;
